import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { PassportSurface } from '../components/passport/PassportSurface';

/**
 * Digital passport page (/passport/$fingerprint), keyed by the public
 * Fingerprint `<prefix>-<token>` - never asset.id. Read-only composition of
 * GET /api/client/passport/:fingerprint (RUF-554). Every part of the document
 * - the §1-§5 stack, print, generate-artifact, the issued-artifacts card -
 * lives in <PassportSurface>.
 *
 * The Fingerprint is a handle, NOT a credential: the route sits behind the
 * portal's normal auth, and the API resolves it under the caller's own RLS
 * session, so a viewer who cannot see the asset gets the same "not available"
 * state as for a bogus value.
 *
 * Two entry points share this page. An owner arrives from the asset detail /
 * the passports registry (no search param): back-link goes to the registry and
 * the generate-artifact button is offered (migration 607 admits the owner's
 * INSERT). A buyer arrives from a listing (`?listing=<id>`): back-link goes to
 * the listing and generate is withheld - a browsing buyer's click would 403
 * from the DB layer; buyers download the artifact the seller minted at publish
 * (migration 777), or print the page.
 */
export const Route = createFileRoute('/passport/$fingerprint')({
  validateSearch: (search: Record<string, unknown>): { listing?: string } => ({
    listing: typeof search.listing === 'string' && search.listing.length > 0 ? search.listing : undefined,
  }),
  component: PassportPage,
});

const BACK_LINK_CLASS =
  'inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground';

function PassportPage() {
  const { fingerprint } = Route.useParams();
  const { listing } = Route.useSearch();

  return (
    <PassportSurface
      fingerprint={fingerprint}
      canGenerate={!listing}
      backLink={
        listing ? (
          <Link to="/listings/$listingId" params={{ listingId: listing }} className={BACK_LINK_CLASS}>
            <ArrowLeft className="size-4" /> Back to listing
          </Link>
        ) : (
          <Link to="/passports" className={BACK_LINK_CLASS}>
            <ArrowLeft className="size-4" /> Digital passports
          </Link>
        )
      }
    />
  );
}
